import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
	DataPointWithSampleRes,
	FieldBindingDto,
	FieldBindingRes,
	FieldBindingResult,
	FieldBindingStateInput,
	PdfBox,
} from './useFieldBindingState.types.ts';

// Local edits are a sparse Partial keyed by fieldKey: a change to the binding
// template or the box doesn't require re-specifying the rest of the row, it's
// layered onto the server row (or the locally-added row) at merge time.
type LocalEdit = Partial<
	Pick<FieldBindingRes, 'bindingTemplate' | 'pageNum' | 'x' | 'y' | 'width' | 'height' | 'label'>
>;

let manualFieldKeySeq = 0;
// Stable-enough key for a freshly-drawn MANUAL box: monotonic counter avoids
// collisions within a single editing session (server assigns the real id on
// save/refetch).
const nextManualFieldKey = (): string => {
	manualFieldKeySeq += 1;
	return `manual-${Date.now()}-${manualFieldKeySeq}`;
};

const applyEdit = (row: FieldBindingRes, edit: LocalEdit | undefined): FieldBindingRes =>
	edit ? { ...row, ...edit } : row;

const toDto = (binding: FieldBindingRes): FieldBindingDto => ({
	fieldKey: binding.fieldKey,
	source: binding.source,
	detectedText: binding.detectedText,
	label: binding.label,
	pageNum: binding.pageNum,
	x: binding.x,
	y: binding.y,
	width: binding.width,
	height: binding.height,
	bindingTemplate: binding.bindingTemplate,
});

// Module-level so an absent query result yields the SAME array every render. A fresh `[]`
// per render would change `bindings`' identity each time and re-run everything downstream.
const NO_BINDINGS: FieldBindingRes[] = [];
const NO_CATALOGUE: DataPointWithSampleRes[] = [];

export const useFieldBindingState = (input: FieldBindingStateInput): FieldBindingResult => {
	const { isLoading, isSaving, newFieldLabel, onSave } = input;
	const serverBindings = input.serverBindings ?? NO_BINDINGS;
	const catalogue = input.catalogue ?? NO_CATALOGUE;

	const [localEdits, setLocalEdits] = useState<Map<string, LocalEdit>>(new Map());
	const [localAdditions, setLocalAdditions] = useState<Map<string, FieldBindingRes>>(new Map());
	const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());
	const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);

	const bindings = useMemo(() => {
		const fromServer = serverBindings
			.filter((row) => !removedKeys.has(row.fieldKey))
			.map((row) => applyEdit(row, localEdits.get(row.fieldKey)));

		const fromLocalAdditions = Array.from(localAdditions.values())
			.filter((row) => !removedKeys.has(row.fieldKey))
			.map((row) => applyEdit(row, localEdits.get(row.fieldKey)));

		return [...fromServer, ...fromLocalAdditions];
	}, [serverBindings, localAdditions, localEdits, removedKeys]);

	// Auto-select the first field once, on initial load, so the panel isn't blank.
	const hasAutoSelected = useRef(false);
	useEffect(() => {
		if (hasAutoSelected.current || bindings.length === 0) {
			return;
		}
		hasAutoSelected.current = true;
		setSelectedFieldKey((current) => current ?? bindings[0].fieldKey);
	}, [bindings]);

	// Re-editing a removed key un-tombstones it (mirrors useSignaturePlacement's
	// onPlacementChange/onPlaceAtDefault behaviour).
	const untombstone = useCallback((fieldKey: string) => {
		setRemovedKeys((prev) => {
			if (!prev.has(fieldKey)) {
				return prev;
			}
			const next = new Set(prev);
			next.delete(fieldKey);
			return next;
		});
	}, []);

	const onChangeBinding = useCallback(
		(fieldKey: string, bindingTemplate: string) => {
			setLocalEdits((prev) => {
				const next = new Map(prev);
				next.set(fieldKey, { ...next.get(fieldKey), bindingTemplate });
				return next;
			});
			untombstone(fieldKey);
		},
		[untombstone]
	);

	const onBoxChange = useCallback(
		(fieldKey: string, pageNum: number, pdfBox: PdfBox) => {
			setLocalEdits((prev) => {
				const next = new Map(prev);
				next.set(fieldKey, { ...next.get(fieldKey), pageNum, ...pdfBox });
				return next;
			});
			untombstone(fieldKey);
		},
		[untombstone]
	);

	const onRenameField = useCallback(
		(fieldKey: string, label: string) => {
			setLocalEdits((prev) => {
				const next = new Map(prev);
				next.set(fieldKey, { ...next.get(fieldKey), label });
				return next;
			});
			untombstone(fieldKey);
		},
		[untombstone]
	);

	const onAddBox = useCallback(
		(pageNum: number, pdfBox: PdfBox) => {
			const fieldKey = nextManualFieldKey();
			const row: FieldBindingRes = {
				id: fieldKey,
				fieldKey,
				source: 'MANUAL',
				detectedText: '',
				label: newFieldLabel,
				pageNum,
				x: pdfBox.x,
				y: pdfBox.y,
				width: pdfBox.width,
				height: pdfBox.height,
				bindingTemplate: null,
			};
			setLocalAdditions((prev) => {
				const next = new Map(prev);
				next.set(fieldKey, row);
				return next;
			});
			setSelectedFieldKey(fieldKey);
		},
		[newFieldLabel]
	);

	const onRemove = useCallback(
		(fieldKey: string) => {
			// A never-saved local addition has no server row to tombstone:
			// dropping the addition is the whole removal, and tombstoning it too
			// would leave the editor dirty with no net change to save.
			if (localAdditions.has(fieldKey)) {
				setLocalAdditions((prev) => {
					const next = new Map(prev);
					next.delete(fieldKey);
					return next;
				});
			} else {
				setRemovedKeys((prev) => {
					const next = new Set(prev);
					next.add(fieldKey);
					return next;
				});
			}
			setLocalEdits((prev) => {
				if (!prev.has(fieldKey)) {
					return prev;
				}
				const next = new Map(prev);
				next.delete(fieldKey);
				return next;
			});
			setSelectedFieldKey((current) => (current === fieldKey ? null : current));
		},
		[localAdditions]
	);

	const discard = useCallback(() => {
		setLocalEdits(new Map());
		setLocalAdditions(new Map());
		setRemovedKeys(new Set());
		setSelectedFieldKey(null);
	}, []);

	const save = useCallback(() => {
		// Returned, not swallowed: a caller saving this alongside something else awaits it.
		// Clear local layers only once the server has accepted, so stale edits
		// don't re-apply on top of the refetched rows.
		return onSave(bindings.map(toDto), () => {
			setLocalEdits(new Map());
			setLocalAdditions(new Map());
			setRemovedKeys(new Set());
		});
	}, [bindings, onSave]);

	// An EMPTY template is not a binding. Generation drops it (`resolveBoundRows` filters `''`),
	// so counting it left the footer reading "3 of 3 bound" over a blank in an executed deed —
	// and the panel emits `''` whenever a mode or scope change leaves no path selected.
	const boundCount = useMemo(
		() =>
			bindings.filter((row) => row.bindingTemplate != null && row.bindingTemplate !== '')
				.length,
		[bindings]
	);
	const totalCount = bindings.length;

	// Any populated local layer means there are edits the server hasn't seen —
	// including a pure deletion, which leaves `bindings` shorter than the
	// server set rather than different from it.
	const isDirty = localEdits.size > 0 || localAdditions.size > 0 || removedKeys.size > 0;

	return {
		bindings,
		catalogue,
		isLoading,
		selectedFieldKey,
		setSelectedFieldKey,
		onChangeBinding,
		onBoxChange,
		onAddBox,
		onRemove,
		onRenameField,
		save,
		discard,
		isDirty,
		isSaving,
		boundCount,
		totalCount,
	};
};
